import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { activeFilterClass } from "../lib/filterStyles";
import {
  UserPlus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  Plus,
  X,
  Link as LinkIcon,
  Unlink,
  KeyRound,
  UserCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const emptyForm = {
  first_name: "",
  last_name: "",
  full_name: "",
  primary_marca_code: "",
  agency_id: "",
  active: true,
};

const emptyAliasForm = {
  source_system: "easy",
  identifier_type: "marca_code",
  raw_value: "",
};

const emptyCreateUserForm = {
  email: "",
  name: "",
  role: "agent",
};

// Afișează aliasurile unui agent pentru o anumită sursă, ca chips (marcă / nume).
const ALIAS_TYPE_SHORT = { marca_code: "cod", full_name: "nume", first_last_name: "nume" };
const renderAliasChips = (identifiers, source) => {
  const list = (identifiers || []).filter((i) => i.source_system === source);
  if (list.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((i) => (
        <span
          key={i.id}
          title={`${i.identifier_type}${i.is_primary ? " • primary" : ""}`}
          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${
            i.is_primary
              ? "border-indigo-300 bg-indigo-50 text-indigo-800"
              : "border-slate-200 bg-slate-50 text-slate-700"
          }`}
        >
          {i.identifier_type === "marca_code" ? (
            <span className="font-mono">{i.raw_value}</span>
          ) : (
            <span>{i.raw_value}</span>
          )}
        </span>
      ))}
    </div>
  );
};

const Agents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("all");

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(null); // null | "new" | agentObject
  const [activeTab, setActiveTab] = useState("details");
  const [form, setForm] = useState(emptyForm);

  // Aliases state
  const [identifiers, setIdentifiers] = useState([]);
  const [aliasForm, setAliasForm] = useState(emptyAliasForm);
  const [aliasLoading, setAliasLoading] = useState(false);

  // Account flows
  const [showLinkUser, setShowLinkUser] = useState(false);
  const [linkUserId, setLinkUserId] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState(emptyCreateUserForm);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [setPasswordValue, setSetPasswordValue] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (agencyFilter && agencyFilter !== "all") params.set("agency_id", agencyFilter);
      params.set("limit", "200");
      const [agRes, agencyRes, userRes] = await Promise.all([
        axios.get(`${API}/agents?${params.toString()}`),
        axios.get(`${API}/agencies`),
        axios.get(`${API}/users`),
      ]);
      setAgents(agRes.data.data || []);
      setAgencies(agencyRes.data || []);
      setUsers(userRes.data || []);
    } catch (e) {
      toast.error("Eroare la încărcarea agenților");
    } finally {
      setLoading(false);
    }
  }, [search, agencyFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isAdmin = user?.role === "admin" || user?.role === "backoffice";

  // Resolve current edited agent (from agents list)
  const currentAgent = editDialog && editDialog !== "new"
    ? agents.find((a) => a.id === editDialog?.id || a.id === editDialog) || editDialog
    : null;

  const openCreate = () => {
    setForm(emptyForm);
    setActiveTab("details");
    setEditDialog("new");
  };

  const openEdit = async (a) => {
    setForm({
      first_name: a.first_name || "",
      last_name: a.last_name || "",
      full_name: a.full_name || "",
      primary_marca_code: a.primary_marca_code || "",
      agency_id: a.agency_id || "",
      active: a.active,
    });
    setEditDialog(a);
    setActiveTab("details");
    setShowLinkUser(false);
    setShowCreateUser(false);
    setShowSetPassword(false);
    setCreateUserForm({ ...emptyCreateUserForm, email: "", name: a.full_name || "" });
    await loadIdentifiers(a.id);
  };

  const closeDialog = () => {
    setEditDialog(null);
    setIdentifiers([]);
    setAliasForm(emptyAliasForm);
    setShowLinkUser(false);
    setShowCreateUser(false);
    setShowSetPassword(false);
  };

  const handleSave = async () => {
    if (!form.full_name && !form.first_name && !form.last_name) {
      toast.error("Numele e obligatoriu");
      return;
    }
    try {
      const payload = { ...form, agency_id: form.agency_id || null };
      if (editDialog === "new") {
        await axios.post(`${API}/agents`, payload);
        toast.success("Agent creat");
        closeDialog();
      } else {
        await axios.put(`${API}/agents/${currentAgent.id}`, payload);
        toast.success("Agent actualizat");
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la salvare");
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await axios.delete(`${API}/agents/${id}`);
      toast.success("Agent dezactivat");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  // ===== ALIASES =====
  const loadIdentifiers = async (agentId) => {
    setAliasLoading(true);
    try {
      const res = await axios.get(`${API}/agents/${agentId}/identifiers`);
      setIdentifiers(res.data || []);
    } catch (e) {
      toast.error("Eroare la încărcarea aliasurilor");
    } finally {
      setAliasLoading(false);
    }
  };

  const handleAddAlias = async () => {
    if (!aliasForm.raw_value.trim()) {
      toast.error("Valoarea aliasului e obligatorie");
      return;
    }
    try {
      await axios.post(`${API}/agents/${currentAgent.id}/identifiers`, aliasForm);
      toast.success("Alias adăugat");
      setAliasForm(emptyAliasForm);
      await loadIdentifiers(currentAgent.id);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    if (!window.confirm("Ștergi acest alias?")) return;
    try {
      await axios.delete(`${API}/agents/${currentAgent.id}/identifiers/${aliasId}`);
      toast.success("Alias șters");
      await loadIdentifiers(currentAgent.id);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  // ===== ACCOUNT (USER) FLOWS =====
  const handleLinkUser = async () => {
    if (!linkUserId) {
      toast.error("Selectează un utilizator");
      return;
    }
    try {
      await axios.post(`${API}/agents/${currentAgent.id}/link-user`, { user_id: linkUserId });
      toast.success("Cont asociat");
      setShowLinkUser(false);
      setLinkUserId("");
      fetchAll();
      // Reload edited agent to refresh local view
      const fresh = (await axios.get(`${API}/agents?limit=200`)).data.data;
      const updated = fresh.find((x) => x.id === currentAgent.id);
      if (updated) setEditDialog(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  const handleUnlinkUser = async () => {
    if (!window.confirm("Dezasociezi contul de acest agent? Agentul va rămâne fără login.")) return;
    try {
      await axios.post(`${API}/agents/${currentAgent.id}/unlink-user`);
      toast.success("Cont dezasociat");
      fetchAll();
      const fresh = (await axios.get(`${API}/agents?limit=200`)).data.data;
      const updated = fresh.find((x) => x.id === currentAgent.id);
      if (updated) setEditDialog(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.email || !createUserForm.email.includes("@")) {
      toast.error("Email invalid");
      return;
    }
    try {
      const res = await axios.post(`${API}/agents/${currentAgent.id}/create-user`, createUserForm);
      toast.success(res.data.message || "Cont creat fără parolă");
      setShowCreateUser(false);
      setCreateUserForm(emptyCreateUserForm);
      fetchAll();
      const fresh = (await axios.get(`${API}/agents?limit=200`)).data.data;
      const updated = fresh.find((x) => x.id === currentAgent.id);
      if (updated) setEditDialog(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  const handleSetPassword = async () => {
    if (setPasswordValue.length < 8) {
      toast.error("Parola trebuie să aibă minim 8 caractere");
      return;
    }
    try {
      await axios.post(`${API}/users/${currentAgent.linked_user_id}/set-password`, {
        password: setPasswordValue,
      });
      toast.success("Parolă setată. Userul o va schimba la primul login.");
      setShowSetPassword(false);
      setSetPasswordValue("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  // Users not linked to any other agent
  const linkedUserIds = new Set(agents.map((a) => a.linked_user_id).filter(Boolean));
  const availableUsers = users.filter((u) => !linkedUserIds.has(u.id));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Agenți</h1>
          <p className="text-slate-500 mt-1">
            Identitate comercială separată — agentul poate exista fără cont de login. Cod Marca propriu și aliasuri per sursă (Easy/CRM/SF).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} data-testid="refresh-agents">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reîncarcă
          </Button>
          {isAdmin && (
            <Button onClick={openCreate} data-testid="new-agent-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Agent Nou
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Caută după nume sau cod Marca..."
                className={`pl-10 ${activeFilterClass(search)}`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-agents-input"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={agencyFilter} onValueChange={setAgencyFilter}>
                <SelectTrigger
                  data-testid="agency-filter-select"
                  className={activeFilterClass(agencyFilter === "all" ? "" : agencyFilter)}
                >
                  <SelectValue placeholder="Filtrează după agenție" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate agențiile</SelectItem>
                  <SelectItem value="__none__" disabled>
                    ───
                  </SelectItem>
                  {agencies.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {agencyFilter !== "all" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAgencyFilter("all")}
                data-testid="clear-agency-filter"
              >
                <X className="h-4 w-4 mr-1" /> Resetează filtru
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Se încarcă...</div>
          ) : agents.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Nu există agenți care să corespundă filtrelor.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nume</TableHead>
                  <TableHead>Cod Marca</TableHead>
                  <TableHead>Agenție</TableHead>
                  <TableHead>Cont login</TableHead>
                  <TableHead>Alias Easy</TableHead>
                  <TableHead>Alias CRM</TableHead>
                  <TableHead>Alias SF</TableHead>
                  <TableHead>Alias Manual</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((a) => (
                  <TableRow key={a.id} data-testid={`agent-row-${a.id}`}>
                    <TableCell className="font-medium">
                      {a.full_name || `${a.last_name || ""} ${a.first_name || ""}`.trim() || "-"}
                    </TableCell>
                    <TableCell>
                      {a.primary_marca_code ? (
                        <Badge variant="outline" className="font-mono">
                          {a.primary_marca_code}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {a.agency_name ? (
                        <span className="inline-flex items-center gap-1.5">
                          {a.agency_name}
                          {a.agency_channel && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                                a.agency_channel === "d2d" ? "bg-indigo-100 text-indigo-700 border-indigo-200"
                                : a.agency_channel === "telesales" ? "bg-cyan-100 text-cyan-700 border-cyan-200"
                                : "bg-amber-100 text-amber-700 border-amber-200"
                              }`}
                              data-testid={`agent-channel-${a.id}`}
                              title="Canal moștenit automat din agenție (obiective & rapoarte)"
                            >
                              {a.agency_channel === "d2d" ? "D2D" : a.agency_channel === "telesales" ? "Telesales" : `Shop ${a.agency_channel.charAt(0).toUpperCase()}${a.agency_channel.slice(1)}`}
                            </span>
                          )}
                        </span>
                      ) : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.linked_user_email ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <UserCheck className="h-3 w-3" /> {a.linked_user_email}
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          fără cont
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell data-testid={`agent-aliases-easy-${a.id}`}>{renderAliasChips(a.identifiers, "easy")}</TableCell>
                    <TableCell data-testid={`agent-aliases-crm-${a.id}`}>{renderAliasChips(a.identifiers, "crm_legacy")}</TableCell>
                    <TableCell data-testid={`agent-aliases-sf-${a.id}`}>{renderAliasChips(a.identifiers, "salesforce")}</TableCell>
                    <TableCell data-testid={`agent-aliases-manual-${a.id}`}>{renderAliasChips(a.identifiers, "manual")}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          a.active
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-600"
                        }
                      >
                        {a.active ? "Activ" : "Inactiv"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(a)}
                            data-testid={`edit-${a.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {a.active && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeactivate(a.id)}
                              data-testid={`delete-${a.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog with Tabs */}
      <Dialog open={!!editDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editDialog === "new" ? "Agent Nou" : `Editează: ${currentAgent?.full_name || ""}`}
            </DialogTitle>
            <DialogDescription>
              Identitate comercială separată. Agentul poate exista fără cont de login.
            </DialogDescription>
          </DialogHeader>

          {editDialog === "new" ? (
            // Simple create form (no tabs needed before agent exists)
            <DetailsForm form={form} setForm={setForm} agencies={agencies} />
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details" data-testid="tab-details">
                  Detalii
                </TabsTrigger>
                <TabsTrigger value="account" data-testid="tab-account">
                  Cont login
                </TabsTrigger>
                <TabsTrigger value="aliases" data-testid="tab-aliases">
                  Aliasuri {identifiers.length > 0 && `(${identifiers.length})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 pt-4">
                <DetailsForm form={form} setForm={setForm} agencies={agencies} />
              </TabsContent>

              <TabsContent value="account" className="space-y-4 pt-4">
                {currentAgent?.linked_user_email ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2 text-emerald-800 font-medium">
                        <UserCheck className="h-5 w-5" /> Cont asociat
                      </div>
                      <p className="text-sm text-emerald-700 mt-1">
                        {currentAgent.linked_user_email}
                      </p>
                    </div>

                    {!showSetPassword ? (
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          onClick={() => setShowSetPassword(true)}
                          data-testid="btn-set-password"
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Setează / Resetează parolă
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleUnlinkUser}
                          data-testid="btn-unlink-user"
                        >
                          <Unlink className="h-4 w-4 mr-2" />
                          Dezasociază cont
                        </Button>
                      </div>
                    ) : (
                      <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                        <Label>Parolă nouă (minim 8 caractere)</Label>
                        <Input
                          type="password"
                          value={setPasswordValue}
                          onChange={(e) => setSetPasswordValue(e.target.value)}
                          placeholder="••••••••"
                          data-testid="input-set-password"
                        />
                        <p className="text-xs text-slate-500">
                          Userul va fi rugat să o schimbe la primul login.
                        </p>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowSetPassword(false);
                              setSetPasswordValue("");
                            }}
                          >
                            Anulează
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSetPassword}
                            data-testid="btn-confirm-set-password"
                          >
                            Salvează parola
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="text-sm text-slate-600">
                        Acest agent <strong>nu are cont de login</strong>. Asociază unul existent
                        sau creează unul nou (fără parolă; o vei seta ulterior).
                      </p>
                    </div>

                    {!showLinkUser && !showCreateUser && (
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          onClick={() => setShowLinkUser(true)}
                          data-testid="btn-link-existing-user"
                        >
                          <LinkIcon className="h-4 w-4 mr-2" />
                          Asociază user existent
                        </Button>
                        <Button
                          onClick={() => setShowCreateUser(true)}
                          data-testid="btn-create-user-for-agent"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Creează user pentru acest agent
                        </Button>
                      </div>
                    )}

                    {showLinkUser && (
                      <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                        <Label>Selectează utilizator existent</Label>
                        <Select value={linkUserId} onValueChange={setLinkUserId}>
                          <SelectTrigger data-testid="select-link-user">
                            <SelectValue placeholder="— alege un user —" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableUsers.length === 0 ? (
                              <SelectItem value="__empty__" disabled>
                                Nu există useri disponibili
                              </SelectItem>
                            ) : (
                              availableUsers.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name || u.email} — {u.email}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">
                          Doar utilizatorii care nu sunt deja asociați altui agent apar în listă.
                        </p>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowLinkUser(false);
                              setLinkUserId("");
                            }}
                          >
                            Anulează
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleLinkUser}
                            data-testid="btn-confirm-link-user"
                          >
                            Asociază
                          </Button>
                        </div>
                      </div>
                    )}

                    {showCreateUser && (
                      <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                        <p className="text-xs text-slate-600">
                          Creează cont fără parolă. Userul va primi acces după ce un admin setează
                          parola.
                        </p>
                        <div>
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={createUserForm.email}
                            onChange={(e) =>
                              setCreateUserForm({ ...createUserForm, email: e.target.value })
                            }
                            placeholder="agent@aqs.ro"
                            data-testid="input-create-user-email"
                          />
                        </div>
                        <div>
                          <Label>Nume afișat</Label>
                          <Input
                            value={createUserForm.name}
                            onChange={(e) =>
                              setCreateUserForm({ ...createUserForm, name: e.target.value })
                            }
                            placeholder="Auto-completat din numele agentului"
                            data-testid="input-create-user-name"
                          />
                        </div>
                        <div>
                          <Label>Rol</Label>
                          <Select
                            value={createUserForm.role}
                            onValueChange={(v) => setCreateUserForm({ ...createUserForm, role: v })}
                          >
                            <SelectTrigger data-testid="select-create-user-role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="agent">Agent</SelectItem>
                              <SelectItem value="agentie">Agenție</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="backoffice">Back Office</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowCreateUser(false);
                              setCreateUserForm(emptyCreateUserForm);
                            }}
                          >
                            Anulează
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleCreateUser}
                            data-testid="btn-confirm-create-user"
                          >
                            Creează cont
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="aliases" className="space-y-3 pt-4">
                <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
                  <p className="text-sm font-medium text-slate-700">Adaugă alias nou</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      value={aliasForm.source_system}
                      onValueChange={(v) => setAliasForm({ ...aliasForm, source_system: v })}
                    >
                      <SelectTrigger data-testid="alias-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="crm_legacy">CRM Legacy</SelectItem>
                        <SelectItem value="salesforce">Salesforce</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={aliasForm.identifier_type}
                      onValueChange={(v) => setAliasForm({ ...aliasForm, identifier_type: v })}
                    >
                      <SelectTrigger data-testid="alias-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marca_code">Cod Marca</SelectItem>
                        <SelectItem value="full_name">Nume complet</SelectItem>
                        <SelectItem value="first_last_name">Prenume + Nume</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Valoare alias"
                      value={aliasForm.raw_value}
                      onChange={(e) => setAliasForm({ ...aliasForm, raw_value: e.target.value })}
                      data-testid="alias-value-input"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={handleAddAlias} data-testid="btn-add-alias">
                      <Plus className="h-4 w-4 mr-1" /> Adaugă
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 max-h-80 overflow-auto">
                  {aliasLoading ? (
                    <p className="text-center text-slate-500 py-4">Se încarcă...</p>
                  ) : identifiers.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4 text-center">
                      Nu există aliasuri. Adaugă unul mai sus sau ele se vor crea automat la
                      următorul import.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sursă</TableHead>
                          <TableHead>Tip</TableHead>
                          <TableHead>Valoare</TableHead>
                          <TableHead className="text-right">Acțiuni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {identifiers.map((i) => (
                          <TableRow key={i.id} data-testid={`alias-row-${i.id}`}>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] uppercase">
                                {i.source_system}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-slate-600">
                              {i.identifier_type}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {i.raw_value}
                              {i.is_primary && (
                                <Badge className="ml-2 bg-blue-50 text-blue-700">primar</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteAlias(i.id)}
                                  data-testid={`btn-delete-alias-${i.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Închide
            </Button>
            {(editDialog === "new" || activeTab === "details") && (
              <Button onClick={handleSave} data-testid="save-agent-btn">
                Salvează
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailsForm = ({ form, setForm, agencies }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Prenume</Label>
        <Input
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          data-testid="form-first-name"
        />
      </div>
      <div>
        <Label>Nume</Label>
        <Input
          value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          data-testid="form-last-name"
        />
      </div>
    </div>
    <div>
      <Label>Nume complet (pentru CRM/SF matching)</Label>
      <Input
        value={form.full_name}
        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
        placeholder="Auto-completat din nume+prenume"
        data-testid="form-full-name"
      />
    </div>
    <div>
      <Label>Cod Marca primar (Easy)</Label>
      <Input
        value={form.primary_marca_code}
        onChange={(e) => setForm({ ...form, primary_marca_code: e.target.value })}
        placeholder="ex: RE12917"
        data-testid="form-marca-code"
      />
    </div>
    <div>
      <Label>Agenție</Label>
      <Select
        value={form.agency_id || "none"}
        onValueChange={(v) => setForm({ ...form, agency_id: v === "none" ? "" : v })}
      >
        <SelectTrigger data-testid="form-agency">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— fără agenție —</SelectItem>
          {agencies.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {a.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="flex items-center gap-3">
      <Switch
        checked={form.active}
        onCheckedChange={(v) => setForm({ ...form, active: v })}
        data-testid="form-active"
      />
      <Label>Activ</Label>
    </div>
  </div>
);

export default Agents;
